import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Lock, Mail, Phone, User, UserPlus } from 'lucide-react';
import { Country } from 'country-state-city';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { SearchableSelect } from './ui/searchable-select';
import { authApi } from '../services/api';
import { getErrorMessage } from '../lib/utils';
import { extractEmailDomain, isCommonEmailDomain } from '../lib/publicEmailDomains';
import {
  getCityOptionsForCountry,
  getCountryIsoCode,
  getPhoneCodeForCountry,
  useCanonicalCountryOptions,
} from '../utils/countries';
import './BoltCargoRegistration.css';

const BOLTCARGO_SOURCE = 'Bolt Member';

function normalizePhoneCode(phonecode) {
  if (!phonecode) return '+1';
  const part = String(phonecode).split('-')[0].trim();
  return part.startsWith('+') ? part : `+${part}`;
}

const initialRegisterForm = {
  email: '',
  password: '',
  confirmPassword: '',
  full_name: '',
  company_name: '',
  phone: '',
  phone_country_code: '+1',
  country: '',
  city: '',
  referral_code: '',
};

export default function BoltCargoRegistration() {
  const navigate = useNavigate();
  const [registerForm, setRegisterForm] = useState(initialRegisterForm);
  const [loading, setLoading] = useState(false);

  const countryList = useCanonicalCountryOptions();
  const countryIsoCode = useMemo(() => getCountryIsoCode(registerForm.country), [registerForm.country]);
  const cityList = useMemo(() => {
    if (!countryIsoCode) return [];
    return getCityOptionsForCountry(registerForm.country);
  }, [countryIsoCode, registerForm.country]);
  const cityIsOther = registerForm.city && !cityList.some((c) => c.value === registerForm.city);
  const citySelectValue = cityIsOther ? '__other__' : (registerForm.city || '');

  const phoneCodeOptions = useMemo(() => {
    const seen = new Set();
    return Country.getAllCountries()
      .filter((c) => c.phonecode)
      .map((c) => {
        const code = normalizePhoneCode(c.phonecode);
        if (seen.has(code)) return null;
        seen.add(code);
        return { value: code, label: `${code} ${c.name}` };
      })
      .filter(Boolean);
  }, []);

  const updateRegisterForm = (updates) => {
    setRegisterForm((prev) => ({ ...prev, ...updates }));
  };

  const handleRegisterCountryChange = (value) => {
    const countryName = value || '';
    updateRegisterForm({
      country: countryName,
      city: '',
      phone_country_code: getPhoneCodeForCountry(countryName),
    });
  };

  const handleRegisterCityChange = (value) => {
    updateRegisterForm({ city: value === '__other__' ? '' : (value || '') });
  };

  const handleRegister = async (event) => {
    event.preventDefault();

    const passwordHasMinLength = registerForm.password.length >= 8;
    const passwordHasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(registerForm.password);

    if (!passwordHasMinLength || !passwordHasSpecialChar) {
      toast.error('Password must be at least 8 characters and include at least one special character');
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (isCommonEmailDomain(registerForm.email)) {
      const domain = extractEmailDomain(registerForm.email);
      toast.error(`Registration requires a company or workspace email. Public email domains like @${domain} are not allowed.`);
      return;
    }

    if (!registerForm.country?.trim()) {
      toast.error('Please select a country');
      return;
    }

    if (!registerForm.city?.trim()) {
      toast.error('Please select or enter a city');
      return;
    }

    const phoneNumber = (registerForm.phone || '').replace(/\s/g, '');
    if (!phoneNumber || phoneNumber.length < 7) {
      toast.error('Phone number is required (at least 7 digits)');
      return;
    }

    setLoading(true);
    try {
      const { confirmPassword, phone_country_code, phone, ...rest } = registerForm;
      const fullPhone = phone.trim().startsWith('+')
        ? phone.trim().replace(/\s/g, '')
        : (phone_country_code || '+1') + phoneNumber;
      const userData = {
        ...rest,
        phone: fullPhone,
        source: BOLTCARGO_SOURCE,
      };
      if (!userData.referral_code || !userData.referral_code.trim()) {
        delete userData.referral_code;
      }

      await authApi.register(userData);
      toast.success('Registration successful! Please check your email to verify your account.');
      setRegisterForm(initialRegisterForm);
      navigate('/login');
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className="boltcargo-page"
      aria-label="BoltCargo global partner program registration"
      style={{ '--boltcargo-bg': "url('/images/boltcargo-kyc-background.jpeg')" }}
    >
      <div className="boltcargo-overlay" />
      <div className="boltcargo-shell">
        <section className="boltcargo-copy" aria-labelledby="boltcargo-title">
          <img
            src="/images/boltcargo-logo.png"
            alt="BoltCargo"
            className="boltcargo-logo"
          />
          <div>
            <h1 id="boltcargo-title">Boltcargo Global Partner Program</h1>
            <p>Building Global Reach Through Trusted Partnership</p>
          </div>
        </section>

        <section className="boltcargo-card" aria-label="Create partner account">
          <div className="boltcargo-verify-pill">Verify Email</div>

          <form onSubmit={handleRegister} className="boltcargo-form">
            <div className="boltcargo-field">
              <Label htmlFor="bolt-reg-name">Full Name *</Label>
              <div className="boltcargo-input-wrap">
                <User size={16} aria-hidden="true" />
                <Input
                  id="bolt-reg-name"
                  value={registerForm.full_name}
                  onChange={(e) => updateRegisterForm({ full_name: e.target.value })}
                  placeholder="John Smith"
                  required
                />
              </div>
            </div>

            <div className="boltcargo-field">
              <Label htmlFor="bolt-reg-company">Company Name *</Label>
              <div className="boltcargo-input-wrap">
                <Building2 size={16} aria-hidden="true" />
                <Input
                  id="bolt-reg-company"
                  value={registerForm.company_name}
                  onChange={(e) => updateRegisterForm({ company_name: e.target.value })}
                  placeholder="Your Company Ltd"
                  required
                />
              </div>
            </div>

            <div className="boltcargo-field">
              <Label htmlFor="bolt-reg-email">Email Address *</Label>
              <div className="boltcargo-input-wrap">
                <Mail size={16} aria-hidden="true" />
                <Input
                  id="bolt-reg-email"
                  type="email"
                  value={registerForm.email}
                  onChange={(e) => updateRegisterForm({ email: e.target.value })}
                  placeholder="you@company.com"
                  required
                />
              </div>
            </div>

            <div className="boltcargo-field">
              <Label htmlFor="bolt-reg-password">Password *</Label>
              <div className="boltcargo-input-wrap">
                <Lock size={16} aria-hidden="true" />
                <Input
                  id="bolt-reg-password"
                  type="password"
                  value={registerForm.password}
                  onChange={(e) => updateRegisterForm({ password: e.target.value })}
                  placeholder="********"
                  minLength={8}
                  required
                />
              </div>
            </div>

            <div className="boltcargo-field">
              <Label htmlFor="bolt-reg-confirm">Confirm Password *</Label>
              <div className="boltcargo-input-wrap">
                <Lock size={16} aria-hidden="true" />
                <Input
                  id="bolt-reg-confirm"
                  type="password"
                  value={registerForm.confirmPassword}
                  onChange={(e) => updateRegisterForm({ confirmPassword: e.target.value })}
                  placeholder="********"
                  required
                />
              </div>
            </div>

            <div className="boltcargo-field-grid">
              <div className="boltcargo-field">
                <Label>Country *</Label>
                <SearchableSelect
                  options={countryList}
                  value={registerForm.country || ''}
                  onChange={handleRegisterCountryChange}
                  placeholder="Select Country"
                  allowOther={false}
                  error={false}
                />
              </div>
              <div className="boltcargo-field">
                <Label>City *</Label>
                <SearchableSelect
                  options={cityList}
                  value={citySelectValue}
                  onChange={handleRegisterCityChange}
                  placeholder={countryIsoCode ? 'Select City' : 'Select country first'}
                  allowOther={!!countryIsoCode}
                  disabled={!countryIsoCode}
                  error={false}
                />
              </div>
            </div>
            {citySelectValue === '__other__' && (
              <div className="boltcargo-field">
                <Label htmlFor="bolt-reg-city-other">City Name *</Label>
                <Input
                  id="bolt-reg-city-other"
                  value={registerForm.city}
                  onChange={(e) => updateRegisterForm({ city: e.target.value })}
                  placeholder="Type city"
                  required
                />
              </div>
            )}

            <div className="boltcargo-field">
              <Label htmlFor="bolt-reg-phone">Phone Number *</Label>
              <div className="boltcargo-phone-row">
                <select
                  id="bolt-reg-phone-code"
                  value={registerForm.phone_country_code}
                  onChange={(e) => updateRegisterForm({ phone_country_code: e.target.value })}
                  aria-label="Country code"
                >
                  {phoneCodeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.value}
                    </option>
                  ))}
                </select>
                <div className="boltcargo-input-wrap">
                  <Phone size={16} aria-hidden="true" />
                  <Input
                    id="bolt-reg-phone"
                    type="tel"
                    value={registerForm.phone}
                    onChange={(e) => updateRegisterForm({ phone: e.target.value })}
                    placeholder="234 567 8900"
                    minLength={7}
                    required
                  />
                </div>
              </div>
              <p className="boltcargo-help">Country code updates when you select a country; you can change it here.</p>
            </div>

            <div className="boltcargo-field">
              <Label htmlFor="bolt-reg-referral">Referral Code (Optional)</Label>
              <Input
                id="bolt-reg-referral"
                value={registerForm.referral_code}
                onChange={(e) => updateRegisterForm({ referral_code: e.target.value })}
                placeholder="e.g. BOLT-OGA-XXXX"
              />
            </div>

            <label className="boltcargo-terms">
              <input type="checkbox" required />
              <span>
                I hereby agree to the{' '}
                <a href="https://oneglobealliance.com/terms-and-conditions/" target="_blank" rel="noreferrer">
                  Terms and Conditions
                </a>{' '}
                and{' '}
                <a href="https://oneglobealliance.com/privacy-policy/" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
                .
              </span>
            </label>

            <Button type="submit" className="boltcargo-submit" disabled={loading}>
              <UserPlus size={17} aria-hidden="true" />
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
        </section>
      </div>

      <footer className="boltcargo-footer">
        <div className="boltcargo-powered">
          <span>Powered by</span>
          <img src="/images/oga-transp-logo.png" alt="One Globe Alliance" />
        </div>
      </footer>
    </main>
  );
}
